import React, { useState, useEffect, useRef } from 'react';
import { Upload, Save, Eye, Trash2, Plus, MousePointer, Image as ImageIcon } from 'lucide-react';
import api from '@/api/api';

const ICardTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);
  
  const [formData, setFormData] = useState({
    template_name: '',
    front_template_url: '',
    back_template_url: '',
    is_default: false,
    field_positions: {
      photo: { x: 20, y: 40, width: 80, height: 100, fontSize: 14 },
      name: { x: 120, y: 50, fontSize: 16, fontWeight: 'bold', color: '#000000' },
      admission_no: { x: 120, y: 75, fontSize: 12, color: '#333333' },
      course: { x: 120, y: 95, fontSize: 11, color: '#666666' },
      roll_no: { x: 120, y: 115, fontSize: 11, color: '#666666' },
      father_name: { x: 120, y: 135, fontSize: 10, color: '#666666' },
      dob: { x: 20, y: 160, fontSize: 10, color: '#666666' },
      blood_group: { x: 150, y: 160, fontSize: 10, color: '#666666' },
      valid_until: { x: 20, y: 180, fontSize: 10, color: '#666666' },
    }
  });

  const availableFields = [
    { key: 'photo', label: 'Student Photo', type: 'image' },
    { key: 'name', label: 'Student Name', type: 'text' },
    { key: 'admission_no', label: 'Admission Number', type: 'text' },
    { key: 'course', label: 'Course Name', type: 'text' },
    { key: 'roll_no', label: 'Roll Number', type: 'text' },
    { key: 'father_name', label: 'Father Name', type: 'text' },
    { key: 'dob', label: 'Date of Birth', type: 'text' },
    { key: 'blood_group', label: 'Blood Group', type: 'text' },
    { key: 'valid_until', label: 'Valid Until', type: 'text' },
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/icard-templates/');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleImageUpload = async (e, side) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setLoading(true);
    try {
      const response = await api.post('/icard-templates/upload-image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (side === 'front') {
        setFormData(prev => ({ ...prev, front_template_url: response.data.image_url }));
      } else {
        setFormData(prev => ({ ...prev, back_template_url: response.data.image_url }));
      }
      
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const handleCanvasClick = (e) => {
    if (!selectedField || !formData.front_template_url) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setFormData(prev => ({
      ...prev,
      field_positions: {
        ...prev.field_positions,
        [selectedField]: {
          ...prev.field_positions[selectedField],
          x: Math.round(x),
          y: Math.round(y)
        }
      }
    }));
  };

  const handleSaveTemplate = async () => {
    if (!formData.template_name || !formData.front_template_url) {
      alert('Please provide template name and front image');
      return;
    }

    setLoading(true);
    try {
      if (currentTemplate) {
        await api.put(`/icard-templates/${currentTemplate.id}`, formData);
        alert('Template updated successfully!');
      } else {
        await api.post('/icard-templates/', formData);
        alert('Template created successfully!');
      }
      
      fetchTemplates();
      setShowEditor(false);
      resetForm();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setCurrentTemplate(template);
    setFormData({
      template_name: template.template_name,
      front_template_url: template.front_template_url,
      back_template_url: template.back_template_url,
      is_default: template.is_default,
      field_positions: template.field_positions
    });
    setShowEditor(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await api.delete(`/icard-templates/${templateId}`);
      alert('Template deleted successfully!');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const resetForm = () => {
    setCurrentTemplate(null);
    setFormData({
      template_name: '',
      front_template_url: '',
      back_template_url: '',
      is_default: false,
      field_positions: {
        photo: { x: 20, y: 40, width: 80, height: 100 },
        name: { x: 120, y: 50, fontSize: 16, fontWeight: 'bold', color: '#000000' },
        admission_no: { x: 120, y: 75, fontSize: 12, color: '#333333' },
        course: { x: 120, y: 95, fontSize: 11, color: '#666666' },
        roll_no: { x: 120, y: 115, fontSize: 11, color: '#666666' },
        father_name: { x: 120, y: 135, fontSize: 10, color: '#666666' },
        dob: { x: 20, y: 160, fontSize: 10, color: '#666666' },
        blood_group: { x: 150, y: 160, fontSize: 10, color: '#666666' },
        valid_until: { x: 20, y: 180, fontSize: 10, color: '#666666' },
      }
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">I-Card Template Manager</h1>
          <p className="text-gray-600 mt-1">Upload custom designs and set data positions</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowEditor(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5" />
          New Template
        </button>
      </div>

      {!showEditor ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div key={template.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg">{template.template_name}</h3>
                {template.is_default && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Default</span>
                )}
              </div>
              
              {template.front_template_url && (
                <img
                  src={template.front_template_url}
                  alt="Template preview"
                  className="w-full h-40 object-contain bg-gray-50 rounded mb-3"
                />
              )}
              
              <div className="text-sm text-gray-600 space-y-1 mb-3">
                <p>Template: <span className="font-medium">{template.template_name}</span></p>
                <p className="text-xs text-gray-500">Card size: 85.6 x 54 mm (Standard)</p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEditTemplate(template)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                >
                  <Eye className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteTemplate(template.id)}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{currentTemplate ? 'Edit' : 'Create'} Template</h2>
            <button
              onClick={() => { setShowEditor(false); resetForm(); }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Back to List
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Settings */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                <input
                  type="text"
                  value={formData.template_name}
                  onChange={(e) => setFormData({...formData, template_name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Student I-Card 2025"
                />
              </div>

              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <p><strong>Note:</strong> Card size is standard (85.6mm x 54mm). Paper size will be selected at print time.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                    className="mr-2"
                  />
                  Set as Default
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Front Template Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'front')}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                {formData.front_template_url && (
                  <img src={formData.front_template_url} alt="Front" className="mt-2 h-32 object-contain" />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Back Template Image (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'back')}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                {formData.back_template_url && (
                  <img src={formData.back_template_url} alt="Back" className="mt-2 h-32 object-contain" />
                )}
              </div>

              <div className="pt-4">
                <button
                  onClick={handleSaveTemplate}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  <Save className="h-5 w-5" />
                  {loading ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>

            {/* Right: Position Mapper */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Click on Image to Position Fields</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Select Field to Position:</label>
                <div className="grid grid-cols-3 gap-2">
                  {availableFields.map((field) => (
                    <button
                      key={field.key}
                      onClick={() => setSelectedField(field.key)}
                      className={`px-3 py-2 text-sm rounded ${
                        selectedField === field.key
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {field.label}
                    </button>
                  ))}
                </div>
              </div>

              {formData.front_template_url && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <canvas
                    ref={canvasRef}
                    onClick={handleCanvasClick}
                    className="cursor-crosshair border border-gray-300"
                    width={400}
                    height={250}
                    style={{
                      backgroundImage: `url(${formData.front_template_url})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center'
                    }}
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    <MousePointer className="inline h-4 w-4" /> Click on the template to set position for selected field
                  </p>
                </div>
              )}

              {selectedField && formData.field_positions[selectedField] && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">{availableFields.find(f => f.key === selectedField)?.label} Settings</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <label className="text-gray-600">X Position:</label>
                      <input
                        type="number"
                        value={formData.field_positions[selectedField].x}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          field_positions: {
                            ...prev.field_positions,
                            [selectedField]: {
                              ...prev.field_positions[selectedField],
                              x: parseInt(e.target.value)
                            }
                          }
                        }))}
                        className="w-full px-2 py-1 border rounded"
                      />
                    </div>
                    <div>
                      <label className="text-gray-600">Y Position:</label>
                      <input
                        type="number"
                        value={formData.field_positions[selectedField].y}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          field_positions: {
                            ...prev.field_positions,
                            [selectedField]: {
                              ...prev.field_positions[selectedField],
                              y: parseInt(e.target.value)
                            }
                          }
                        }))}
                        className="w-full px-2 py-1 border rounded"
                      />
                    </div>
                    {formData.field_positions[selectedField].fontSize && (
                      <div>
                        <label className="text-gray-600">Font Size:</label>
                        <input
                          type="number"
                          value={formData.field_positions[selectedField].fontSize}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            field_positions: {
                              ...prev.field_positions,
                              [selectedField]: {
                                ...prev.field_positions[selectedField],
                                fontSize: parseInt(e.target.value)
                              }
                            }
                          }))}
                          className="w-full px-2 py-1 border rounded"
                        />
                      </div>
                    )}
                    {formData.field_positions[selectedField].color && (
                      <div>
                        <label className="text-gray-600">Color:</label>
                        <input
                          type="color"
                          value={formData.field_positions[selectedField].color}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            field_positions: {
                              ...prev.field_positions,
                              [selectedField]: {
                                ...prev.field_positions[selectedField],
                                color: e.target.value
                              }
                            }
                          }))}
                          className="w-full h-8 border rounded"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ICardTemplateManager;
